import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import httpClient from "../services/http-client";
import { systemService } from "../services/system.service";
import Dialog from "../components/ui/Dialog";
import MaintenancePage from "../components/MaintenancePage";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";

interface SystemContextType {
  isMaintenanceMode: boolean;
  checkMaintenanceStatus: () => Promise<void>;
  toggleMaintenance: (enabled: boolean) => Promise<void>;
  showError: (message: string) => void;
}

const SystemContext = createContext<SystemContextType | undefined>(undefined);

export const SystemProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [errorDialog, setErrorDialog] = useState<{
    isOpen: boolean;
    message: string;
  }>({
    isOpen: false,
    message: "",
  });
  const { t } = useTranslation("common");
  const { user } = useAuth(); // To check if user is admin, though backend handles bypass

  const isAdmin = user?.role === "ADMIN";

  const checkMaintenanceStatus = useCallback(async () => {
    try {
      const status = await systemService.getStatus();
      setIsMaintenanceMode(status.maintenance);
    } catch (error) {
      console.error("Failed to check system status", error);
    }
  }, []);

  const toggleMaintenance = async (enabled: boolean) => {
    const result = await systemService.toggleMaintenance(enabled);
    setIsMaintenanceMode(result.maintenance);
  };

  const showError = useCallback(
    (message: string) => {
      // Translate error tag if possible
      // Assuming error message is a translation key
      const translatedMessage = t(`errors.${message}`, {
        defaultValue: message,
      });
      setErrorDialog({ isOpen: true, message: translatedMessage });
    },
    [t],
  );

  const closeErrorDialog = () => {
    setErrorDialog((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    // Check status on mount
    checkMaintenanceStatus();

    // Set up listeners
    httpClient.setMaintenanceListener(() => {
      setIsMaintenanceMode(true);
    });

    httpClient.setErrorListener((error: any) => {
      const message =
        error.response?.data?.message ||
        error.message ||
        "An unknown error occurred";
      showError(message);
    });

    return () => {
      httpClient.setMaintenanceListener(() => {});
      httpClient.setErrorListener(() => {});
    };
  }, [checkMaintenanceStatus, showError]);

  // If maintenance mode is active and user is NOT admin
  // Allow access to login page and admin routes to enable disabling maintenance
  const isExcludedPath =
    window.location.pathname === "/login" ||
    window.location.pathname.startsWith("/admin");

  if (isMaintenanceMode && !isAdmin && !isExcludedPath) {
    return <MaintenancePage />;
  }

  return (
    <SystemContext.Provider
      value={{
        isMaintenanceMode,
        checkMaintenanceStatus,
        toggleMaintenance,
        showError,
      }}
    >
      {children}
      <Dialog
        isOpen={errorDialog.isOpen}
        onClose={closeErrorDialog}
        title={t("error")}
      >
        <p>{errorDialog.message}</p>
      </Dialog>
    </SystemContext.Provider>
  );
};

export const useSystem = () => {
  const context = useContext(SystemContext);
  if (context === undefined) {
    throw new Error("useSystem must be used within a SystemProvider");
  }
  return context;
};
